import type { IncomeFile } from './types.d';

export interface DownloadFileParams {
  file: IncomeFile;
  loanId: string;
}

/**
 * Downloads a file from the income clarity results
 * @param params - File and loan information
 * @returns Promise<Blob> - The downloaded file as a blob
 */
export const downloadFile = async ({
  file,
  loanId,
}: DownloadFileParams): Promise<Blob> => {
  try {
    // Get the auth token for the request
    const { getAuthToken } = await import('../utils/authTokenHttp');
    const token = await getAuthToken();

    // Get runtime config for API URL
    const { getRuntimeConfig } = await import('../../utils/runtimeConfig');
    const runtimeConfig = getRuntimeConfig();
    const apiUrl = runtimeConfig.VITE_MOS_API_URL;

    // Get tenant ID
    const { getCurrentTenantId } = await import('../../stores/tenant');
    const tenantId = getCurrentTenantId();

    // Make direct fetch request for blob download
    const response = await fetch(
      `${apiUrl}/loans/${loanId}/income-clarity/files/${file.id}/download`,
      {
        method: 'GET',
        headers: {
          Accept: file.fileType,
          'tenant-id': tenantId || '',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();

    if (!blob || blob.size === 0) {
      throw new Error('No file data received from server');
    }

    return blob;
  } catch (error) {
    throw new Error(
      `Failed to download file ${file.fileName}: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
};

export { triggerFileDownload } from '../../utils/fileDownload';
export default downloadFile;
